"use client"

import { useId, useRef, useState } from "react"
import { Input, Tabs, TabsContent, TabsList, TabsTrigger, Textarea } from "@ui/shad"
import { FileText, Upload } from "lucide-react"
import { toast } from "sonner"

import { useCreateMeetingTranscript } from "@/hooks/api/meetings/mutations"
import { ConfirmDialog, SimpleFormDialog } from "@/components/dialogs"
import { PartnerFeatureDialogGate } from "@/components/partners/partner-feature-gate"

export const MAX_TRANSCRIPT_LENGTH = 500_000

interface UploadTranscriptDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  meetingId: string
  hasExistingTranscript?: boolean
}

function UploadTranscriptDialogContent({
  open,
  setOpen,
  meetingId,
  hasExistingTranscript,
}: UploadTranscriptDialogProps) {
  const dialogFormId = useId()

  const [tab, setTab] = useState<"paste" | "file">("file")
  const [pastedText, setPastedText] = useState("")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const createTranscript = useCreateMeetingTranscript()

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setPastedText("")
      setSelectedFile(null)
      setShowConfirmation(false)
    }
    setOpen(newOpen)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const validTypes = [".txt", ".vtt"]
      const extension = file.name.toLowerCase().slice(file.name.lastIndexOf("."))
      if (!validTypes.includes(extension)) {
        toast.error("Please select a .txt or .vtt file")
        return
      }
      setSelectedFile(file)
    }
  }

  const handleUpload = async () => {
    if (hasExistingTranscript && !showConfirmation) {
      setShowConfirmation(true)
      return
    }

    let text = ""

    if (tab === "paste") {
      text = pastedText.trim()
    } else if (selectedFile) {
      text = await selectedFile.text()
    }

    if (!text) {
      toast.error("Please provide transcript text")
      return
    }

    if (text.length > MAX_TRANSCRIPT_LENGTH) {
      toast.error(`Transcript exceeds maximum length of ${MAX_TRANSCRIPT_LENGTH.toLocaleString()} characters`)
      return
    }

    try {
      await createTranscript.mutateAsync({ meetingId, text })
      toast.success("Transcript uploaded successfully")
      handleOpenChange(false)
    } catch {
      // Error is handled by mutation's meta.errorToast
    }
  }

  const isDisabled = tab === "paste" ? !pastedText.trim() || pastedText.length > MAX_TRANSCRIPT_LENGTH : !selectedFile
  const isPending = createTranscript.isPending

  if (showConfirmation) {
    return (
      <ConfirmDialog
        dismiss="explicit"
        title={"Replace existing transcript?"}
        open={open}
        onOpenChange={handleOpenChange}
        description="This meeting already has a transcript. Uploading a new one will replace it. This action cannot be undone."
        action={{
          label: "Replace transcript",
          onClick: handleUpload,
          disabled: isDisabled || isPending,
        }}
        cancel={{ onClick: () => handleOpenChange(false), disabled: isPending }}
      ></ConfirmDialog>
    )
  }

  return (
    <SimpleFormDialog
      dismiss="explicit"
      title={"Upload transcript"}
      open={open}
      onOpenChange={handleOpenChange}
      description={"Upload a transcript file (.txt, .vtt) or paste the text directly."}
      action={{
        label: "Upload transcript",
        pending: isPending,
        form: dialogFormId,
        disabled: isDisabled || isPending,
      }}
      cancel={{ label: "Cancel", onClick: () => handleOpenChange(false), disabled: isPending }}
    >
      <form
        id={dialogFormId}
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (isDisabled || isPending) return
          void handleUpload()
        }}
      >
        <Tabs value={tab} onValueChange={(v) => setTab(v as "paste" | "file")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="file" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload file
            </TabsTrigger>
            <TabsTrigger value="paste" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Paste text
            </TabsTrigger>
          </TabsList>

          <TabsContent value="file" className="mt-4">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".txt,.vtt"
              onChange={handleFileChange}
              className="border-muted-foreground/40 bg-background file:bg-secondary file:text-secondary-foreground hover:border-muted-foreground focus-visible:ring-ring h-12 cursor-pointer rounded-lg border border-dashed text-sm file:mr-4 file:rounded-md file:border-0 file:px-3 file:py-2 file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2"
            />
            {selectedFile && <p className="text-muted-foreground mt-2 pl-1 text-xs">Selected: {selectedFile.name}</p>}
          </TabsContent>

          <TabsContent value="paste" className="mt-4 space-y-2">
            <Textarea
              placeholder="Paste your transcript here..."
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              rows={10}
              className="resize-none"
            />
            {pastedText.length > MAX_TRANSCRIPT_LENGTH && (
              <p className="text-destructive text-xs">
                Transcript exceeds maximum length of {MAX_TRANSCRIPT_LENGTH.toLocaleString()} characters (
                {pastedText.length.toLocaleString()} entered)
              </p>
            )}
          </TabsContent>
        </Tabs>
      </form>
    </SimpleFormDialog>
  )
}

export function UploadTranscriptDialog(props: React.ComponentProps<typeof UploadTranscriptDialogContent>) {
  return (
    <PartnerFeatureDialogGate
      feature="notetaker"
      title="Upload transcript"
      open={props.open}
      onOpenChange={props.setOpen}
    >
      <UploadTranscriptDialogContent {...props} />
    </PartnerFeatureDialogGate>
  )
}
